class BrandsList extends HTMLElement {
  constructor() {
    super();

    this.brandsData = this.querySelector('script[id="BrandsData"]');
    this.brands = JSON.parse(this.brandsData.textContent).brands;

    this.listingContainer = this.querySelector('.brands__listing');
    this.groupingsContainer = this.querySelector('.brands__groupings');

    this.letters = [
      'a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j', 'k',
      'l', 'm', 'n', 'o', 'p', 'q', 'r', 's', 't', 'u', 'v',
      'w', 'x', 'y', 'z', '#'
    ];
  }

  connectedCallback() {
    this.groups = this.groupBrands();
    this.renderListing();
    this.renderGroupings();
  }

  groupBrands() {
    // Case-insensitive, locale-aware sort so accented characters land in
    // sensible places (COMME des GARÇONS sorts under C, etc.).
    const sorted = [...this.brands].sort((a, b) =>
      a.title.localeCompare(b.title, undefined, { sensitivity: 'base' })
    );

    // Bucket each brand under its starting letter; anything not a-z (digits,
    // symbols) goes under "#".
    const groups = Object.fromEntries(this.letters.map((l) => [l, []]));
    sorted.forEach((brand) => {
      const first = brand.title.charAt(0).toLowerCase();
      const key = /[a-z]/.test(first) ? first : '#';
      groups[key].push(brand);
    });

    return groups;
  }

  // "#" isn't valid in an id, so map it to a safe slug.
  groupId(letter) {
    return `brands-group-${letter === '#' ? 'hash' : letter}`;
  }

  renderListing() {
    // Wrap the letters in a real list so assistive tech announces it as
    // "list, 27 items" rather than a stream of loose links.
    const list = document.createElement('ul');
    list.classList.add('brands__listing-list');

    this.letters.forEach((letter) => {
      const hasBrands = this.groups[letter].length > 0;

      const li = document.createElement('li');
      li.classList.add('brands__listing-item');

      // <a> when the letter has brands (so it can navigate / be deep-linked),
      // <span> when empty (no nav target, still styleable as "disabled").
      const inner = document.createElement(hasBrands ? 'a' : 'span');
      inner.classList.add('brands__listing-link');
      inner.textContent = letter.toUpperCase();

      if (hasBrands) {
        inner.href = `#${this.groupId(letter)}`;
      } else {
        inner.classList.add('brands__listing-link--empty');
        inner.setAttribute('aria-disabled', 'true');
      }

      li.appendChild(inner);
      list.appendChild(li);
    });

    this.listingContainer.appendChild(list);
  }

  renderGroupings() {
    const fragment = document.createDocumentFragment();

    this.letters.forEach((letter) => {
      const brands = this.groups[letter];
      if (brands.length === 0) return;

      const group = document.createElement('section');
      group.id = this.groupId(letter);
      group.classList.add('brands__group');
      group.setAttribute('aria-labelledby', `${this.groupId(letter)}-heading`);

      // Use an <h2> so screen reader users can jump between letter groups
      // via their headings-navigation shortcut. The page's <h1> ("Brands")
      // already sits above, so h2 is the correct level here.
      const heading = document.createElement('h2');
      heading.id = `${this.groupId(letter)}-heading`;
      heading.classList.add('brands__group-letter');
      heading.textContent = letter.toUpperCase();
      group.appendChild(heading);

      const list = document.createElement('ul');
      list.classList.add('brands__group-list');

      brands.forEach((brand) => {
        const li = document.createElement('li');
        li.classList.add('brands__group-item');

        const link = document.createElement('a');
        link.classList.add('brands__group-link');
        link.href = brand.url || '#';
        link.textContent = brand.title;

        li.appendChild(link);
        list.appendChild(li);
      });

      group.appendChild(list);
      fragment.appendChild(group);
    });

    this.groupingsContainer.appendChild(fragment);
  }
}

customElements.define('brands-list', BrandsList);
